import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import type { Note } from '@/types'
import type { FirestorePageResult } from '@/hooks/useFirestorePagination'

export const HOME_NOTES_LIMIT = 10
export const STORE_NOTES_PAGE_SIZE = 20

const col = collection(db, COLLECTIONS.notes)

function mapNote(id: string, data: Record<string, unknown>): Note {
  return {
    id,
    name: (data.name as string) || '',
    image: (data.image as string) || '',
    createdAt: (data.createdAt as string) || new Date().toISOString(),
    updatedAt: (data.updatedAt as string) || new Date().toISOString(),
  }
}

function notesOrderedQuery() {
  return query(col, orderBy('name', 'asc'))
}

export async function fetchNotes(): Promise<Note[]> {
  const snap = await getDocs(notesOrderedQuery())
  return snap.docs.map((d) => mapNote(d.id, d.data()))
}

export async function countNotes(): Promise<number> {
  const snap = await getCountFromServer(notesOrderedQuery())
  return snap.data().count
}

export async function fetchNotesPage(opts: {
  pageSize: number
  startAfter: QueryDocumentSnapshot | null
}): Promise<FirestorePageResult<Note>> {
  const base = notesOrderedQuery()
  const q =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(q)
  const items = snap.docs.map((d) => mapNote(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null

  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

export async function fetchHomeNotes(): Promise<{
  notes: Note[]
  hasMore: boolean
}> {
  const [page, total] = await Promise.all([
    fetchNotesPage({ pageSize: HOME_NOTES_LIMIT, startAfter: null }),
    countNotes(),
  ])

  return {
    notes: page.items,
    hasMore: total > HOME_NOTES_LIMIT,
  }
}

export async function createNote(
  data: Pick<Note, 'name' | 'image'>
): Promise<string> {
  const now = new Date().toISOString()
  const ref = await addDoc(col, {
    name: data.name,
    image: data.image,
    createdAt: now,
    updatedAt: now,
  })
  return ref.id
}

export async function updateNote(
  id: string,
  data: Pick<Note, 'name' | 'image'>
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.notes, id), {
    name: data.name,
    image: data.image,
    updatedAt: new Date().toISOString(),
  })
}

export async function deleteNote(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.notes, id))
}
